import { useState, useEffect } from 'react'
import { rapportinoTemplatesApi, type RapportinoTemplate, type CampoTemplate } from '../services/rapportinoTemplates'

export interface ValoriRapportino {
  [campoId: string]: any
}

interface UseTemplateRapportinoResult {
  template: RapportinoTemplate | null
  loading: boolean
  error: string | null
  valori: ValoriRapportino
  setValore: (campoId: string, valore: any) => void
  resetValori: () => void
  validaObbligatori: () => { valido: boolean; campiMancanti: string[] }
  buildFormData: (existingFd: FormData) => FormData
}

function initValori(campi: CampoTemplate[]): ValoriRapportino {
  const v: ValoriRapportino = {}
  for (const c of campi) {
    switch (c.tipo) {
      case 'checkbox':   v[c.id] = null; break
      case 'foto':       v[c.id] = []; break
      case 'firma':      v[c.id] = null; break
      case 'materiali':  v[c.id] = null; break
      case 'select':     v[c.id] = c.scelte_multiple ? [] : ''; break
      default:           v[c.id] = ''
    }
  }
  return v
}

export function useTemplateRapportino(workOrderId: number | null): UseTemplateRapportinoResult {
  const [template, setTemplate] = useState<RapportinoTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [valori, setValori] = useState<ValoriRapportino>({})

  useEffect(() => {
    if (!workOrderId) return
    setLoading(true)
    setError(null)
    rapportinoTemplatesApi.getByWorkOrder(workOrderId)
      .then(t => {
        t.campi = [...t.campi].sort((a, b) => a.ordine - b.ordine)
        setTemplate(t)
        setValori(initValori(t.campi))
      })
      .catch((err: any) => {
        if (err?.response?.status === 404) {
          setError('NO_TEMPLATE')
        } else {
          setError('Errore nel caricamento del template')
        }
      })
      .finally(() => setLoading(false))
  }, [workOrderId])

  const setValore = (campoId: string, valore: any) =>
    setValori(prev => ({ ...prev, [campoId]: valore }))

  const resetValori = () => {
    if (template) setValori(initValori(template.campi))
  }

  const validaObbligatori = () => {
    if (!template) return { valido: true, campiMancanti: [] }
    const mancanti: string[] = []
    for (const c of template.campi) {
      if (!c.obbligatorio) continue
      const v = valori[c.id]
      const vuoto = v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
      if (vuoto) mancanti.push(c.etichetta)
    }
    return { valido: mancanti.length === 0, campiMancanti: mancanti }
  }

  const buildFormData = (existingFd: FormData): FormData => {
    if (!template) return existingFd
    const valoriSemplici: Record<string, any> = {}
    for (const campo of template.campi) {
      const v = valori[campo.id]
      if (campo.tipo === 'foto') {
        const files: File[] = Array.isArray(v) ? v.filter(Boolean) : []
        files.forEach((file, idx) => existingFd.append(`foto_${campo.id}_${idx}`, file))
        valoriSemplici[campo.id] = { tipo: 'foto', count: files.length, label: campo.etichetta }
      } else if (campo.tipo === 'firma') {
        if (v instanceof Blob) {
          existingFd.append(`firma_${campo.id}`, v, `firma_${campo.id}.png`)
          valoriSemplici[campo.id] = { tipo: 'firma', label: campo.etichetta }
        }
      } else if (campo.tipo === 'materiali' || campo.tipo === 'separatore') {
        continue
      } else {
        valoriSemplici[campo.id] = v
      }
    }
    existingFd.append('template_id', String(template.id))
    existingFd.append('template_valori', JSON.stringify(valoriSemplici))
    return existingFd
  }

  return { template, loading, error, valori, setValore, resetValori, validaObbligatori, buildFormData }
}
